import { useMemo, useState, useEffect } from 'react';

export interface UseAdvancedPagingOptions {
    defaultItemsPerPage?: number;
}

export interface UseAdvancedPaging<T> {
    currentPage: number;
    totalPages: number;
    currentData: T[];
    itemsPerPage: number | 'All';
    totalItems: number;
    startIndex: number;
    endIndex: number;
    nextPage: () => void;
    prevPage: () => void;
    goToPage: (page: number) => void;
    setItemsPerPage: (itemsPerPage: number | 'All') => void;
    setCurrentPage: (page: number) => void;
    canGoNext: boolean;
    canGoPrev: boolean;
    pageNumbers: number[];
    showPagination: boolean;
}

function useAdvancedPaging<T>(data: T[], options: UseAdvancedPagingOptions = {}): UseAdvancedPaging<T> {
    const { defaultItemsPerPage = 20 } = options;

    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number | 'All'>(defaultItemsPerPage);

    const totalItems = data.length;
    const effectiveItemsPerPage = itemsPerPage === 'All' ? totalItems : itemsPerPage;
    const totalPages = itemsPerPage === 'All' ? 1 : Math.ceil(totalItems / effectiveItemsPerPage);
    const startIndex = itemsPerPage === 'All' ? 0 : (currentPage - 1) * effectiveItemsPerPage;
    const endIndex = itemsPerPage === 'All' ? totalItems : Math.min(startIndex + effectiveItemsPerPage, totalItems);

    const currentData = itemsPerPage === 'All' ? data : data.slice(startIndex, endIndex);

    const canGoNext = currentPage < totalPages;
    const canGoPrev = currentPage > 1;
    const showPagination = totalPages > 1;

    const nextPage = () => {
        if (canGoNext) {
            setCurrentPage((prev) => prev + 1);
        }
    };

    const prevPage = () => {
        if (canGoPrev) {
            setCurrentPage((prev) => prev - 1);
        }
    };

    const goToPage = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleSetItemsPerPage = (newItemsPerPage: number | 'All') => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
    };

    const pageNumbers = useMemo(() => {
        const pages: number[] = [];
        const maxVisiblePages = 7;

        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            const current = currentPage;
            const total = totalPages;

            if (current <= 4) {
                for (let i = 1; i <= 5; i++) {
                    pages.push(i);
                }
                if (total > 6) {
                    pages.push(-1);
                }
                if (total > 5) {
                    pages.push(total);
                }
            } else if (current >= total - 3) {
                pages.push(1);
                if (total > 6) {
                    pages.push(-1);
                }
                for (let i = total - 4; i <= total; i++) {
                    pages.push(i);
                }
            } else {
                pages.push(1);
                pages.push(-1);
                for (let i = current - 1; i <= current + 1; i++) {
                    pages.push(i);
                }
                pages.push(-1);
                pages.push(total);
            }
        }

        return pages;
    }, [currentPage, totalPages]);

    useEffect(() => {
        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(1);
        }
    }, [totalPages, currentPage]);

    return {
        currentPage,
        totalPages,
        currentData,
        itemsPerPage,
        totalItems,
        startIndex,
        endIndex,
        nextPage,
        prevPage,
        goToPage,
        setItemsPerPage: handleSetItemsPerPage,
        setCurrentPage,
        canGoNext,
        canGoPrev,
        pageNumbers,
        showPagination,
    };
}

export default useAdvancedPaging;
